import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE } from '../config';
import { api } from '../api';
import { useTheme } from '../context/ThemeContext';
import { useSocket } from '../context/SocketContext';
import { useAlert } from '../hooks/useAlert';
import { useToast } from '../context/ToastContext';
import { SkeletonGrid } from '../components/Skeleton';
import { EmptyState } from '../components/UI';
import Button from '../components/Button';
import Icon from '../components/Icon';
import Modal from '../components/Modal';
import CoachMark from '../components/CoachMark';

interface Game {
  id: string;
  name: string;
  imageUrl: string | null;
  downloadLink: string | null;
  isHot?: boolean;
  credentialRequestsEnabled?: boolean;
}

interface Credential {
  id: string;
  gameUsername: string;
  gamePasswordPlain: string;
  assignedAt: string;
  game: { name: string };
}

type GameStatus = 'ready' | 'pending' | 'rejected' | 'request';
type FilterTab = 'all' | 'hot';

function allowsCredentialRequests(game: Game) {
  return game.credentialRequestsEnabled !== false;
}

function shouldShowGame(game: Game) {
  return allowsCredentialRequests(game) || !!game.downloadLink;
}

export default function Games() {
  const { t } = useTranslation();
  const { colors, isDark } = useTheme();
  const { alert } = useAlert();
  const toast = useToast();
  const { credentialUpdateCount } = useSocket();

  const [games, setGames] = useState<Game[]>([]);
  const [statusByGame, setStatusByGame] = useState<Record<string, GameStatus>>({});
  const [requestReasonsByGame, setRequestReasonsByGame] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState<FilterTab>('all');
  const [search, setSearch] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [loading, setLoading] = useState(true);

  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [credLoading, setCredLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [requestingId, setRequestingId] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState('');

  const [showRequestModal, setShowRequestModal] = useState(false);
  const [submittingRequest, setSubmittingRequest] = useState(false);

  const fetchGames = useCallback(async () => {
    try {
      const [gamesRes, credsRes, reqsRes] = await Promise.all([
        axios.get<Game[]>(`${API_BASE}/games`),
        api.get<{ gameId: string }[]>('/games/my-credentials').catch(() => ({ data: [] } as any)),
        api.get<{ gameId: string; status: string; reason?: string | null }[]>('/games/my-credential-requests').catch(() => ({ data: [] } as any)),
      ]);
      setGames(gamesRes.data.filter(shouldShowGame));
      const status: Record<string, GameStatus> = {};
      const reasons: Record<string, string> = {};
      (credsRes.data || []).forEach((c: any) => { if (c.gameId) status[c.gameId] = 'ready'; });
      (reqsRes.data || []).forEach((r: any) => {
        if (!r.gameId || status[r.gameId] === 'ready' || status[r.gameId]) return;
        if (r.status === 'Pending') status[r.gameId] = 'pending';
        if (r.status === 'Rejected') {
          status[r.gameId] = 'rejected';
          reasons[r.gameId] = r.reason || 'No reason provided.';
        }
      });
      gamesRes.data.filter(allowsCredentialRequests).forEach(g => { if (!status[g.id]) status[g.id] = 'request'; });
      setStatusByGame(status);
      setRequestReasonsByGame(reasons);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchGames(); }, [fetchGames]);

  useEffect(() => {
    if (!showModal || !selectedGame) return;
    setCredLoading(true);
    api.get<Credential[]>(`/games/${selectedGame.id}/my-credentials`)
      .then(res => setCredentials(res.data))
      .catch(() => null)
      .finally(() => setCredLoading(false));
  }, [credentialUpdateCount, showModal, selectedGame]);

  const openCredentials = async (game: Game) => {
    setSelectedGame(game);
    setCredentials([]);
    setShowModal(true);
    setCredLoading(true);
    try {
      const res = await api.get<Credential[]>(`/games/${game.id}/my-credentials`);
      setCredentials(res.data);
    } catch {
      /* handled by empty state */
    } finally {
      setCredLoading(false);
    }
  };

  const handleDownload = (link: string) => window.open(link, '_blank', 'noopener');

  const requestPasswordReset = (cred: Credential) => {
    alert(
      t('games.passwordReset.confirmTitle'),
      t('games.passwordReset.confirmMessage', { username: cred.gameUsername }),
      [
        { text: t('games.passwordReset.cancel'), style: 'cancel' },
        {
          text: t('games.passwordReset.send'),
          onPress: async () => {
            setRequestingId(cred.id);
            try {
              await api.post(`/games/my-credentials/${cred.id}/reset-request`, {});
              toast.success(t('games.passwordReset.sentTitle'), t('games.passwordReset.sentMessage'));
            } catch {
              alert(t('games.passwordReset.errorTitle'), t('games.passwordReset.errorMessage'));
            } finally {
              setRequestingId(null);
            }
          },
        },
      ],
    );
  };

  const copyCredentialValue = async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      if (navigator.vibrate) navigator.vibrate(40);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(''), 1600);
    } catch {
      toast.error(t('games.copy.failedTitle'), t('games.copy.failedMessage'));
    }
  };

  const submitCredentialRequest = async (game: Game) => {
    setSubmittingRequest(true);
    try {
      await api.post('/games/credential-requests', { gameId: game.id });
      alert(t('games.requestSent.title'), t('games.requestSent.message', { game: game.name }));
      setStatusByGame(prev => ({ ...prev, [game.id]: 'pending' }));
      setRequestReasonsByGame(prev => {
        const next = { ...prev };
        delete next[game.id];
        return next;
      });
      fetchGames();
    } catch {
      alert(t('games.requestError.title'), t('games.requestError.message'));
    } finally {
      setSubmittingRequest(false);
    }
  };

  const showRejectedRequest = (game: Game) => {
    const reason = requestReasonsByGame[game.id] || t('games.rejected.noReason');
    alert(
      t('games.rejected.title'),
      t('games.rejected.message', { game: game.name, reason }),
      [
        { text: t('games.rejected.close'), style: 'cancel' },
        { text: t('games.rejected.requestAgain'), onPress: () => confirmCredentialRequest(game) },
      ],
    );
  };

  const confirmCredentialRequest = (game: Game) => {
    alert(
      t('games.confirmRequest.title'),
      t('games.confirmRequest.message', { game: game.name }),
      [
        { text: t('games.confirmRequest.cancel'), style: 'cancel' },
        {
          text: t('games.confirmRequest.send'),
          onPress: () => {
            setShowRequestModal(false);
            submitCredentialRequest(game);
          },
        },
      ],
    );
  };

  const visibleGames = games
    .filter(g => filter === 'all' || g.isHot)
    .filter(g => g.name.toLowerCase().includes(search.trim().toLowerCase()));

  const showFloatingCta = games
    .filter(allowsCredentialRequests)
    .some(game => ['request', 'rejected'].includes(statusByGame[game.id] || 'request'));

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: colors.bg }}>
      <div
        data-tour="games-header"
        style={{
          padding: '20px 20px 12px',
          paddingTop: 'calc(20px + env(safe-area-inset-top, 0px))',
          backgroundColor: colors.card,
          borderBottom: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`,
          position: 'sticky', top: 0, zIndex: 10,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: colors.text }}>{t('games.header')}</div>
          <button
            aria-label={t('games.toggleSearch')}
            onClick={() => setShowSearch(s => !s)}
            style={{
              width: 44, height: 44, borderRadius: 22,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'transparent', border: 'none', cursor: 'pointer',
            }}
          >
            <Icon name="search" size={16} color={colors.text} />
          </button>
        </div>

        {showSearch && (
          <div style={{
            display: 'flex', alignItems: 'center',
            backgroundColor: colors.bg, border: `1px solid ${colors.cardBorder}`,
            borderRadius: 12, padding: '0 12px', marginTop: 12, minHeight: 44,
          }}>
            <Icon name="search" size={13} color={colors.textMuted} style={{ marginRight: 8 }} />
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('games.searchPlaceholder')}
              autoCapitalize="none"
              style={{
                flex: 1, padding: '8px 0', fontSize: 14,
                color: colors.text, background: 'transparent', border: 'none', outline: 'none',
              }}
            />
            {search && (
              <button
                aria-label={t('games.clearSearch')}
                onClick={() => setSearch('')}
                style={{
                  width: 44, height: 44, marginRight: -12,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'transparent', border: 'none', cursor: 'pointer',
                }}
              >
                <Icon name="times" size={13} color={colors.textMuted} />
              </button>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
          {([
            { id: 'all', label: t('games.filters.all') },
            { id: 'hot', label: t('games.filters.hot') },
          ] as { id: FilterTab; label: string }[]).map(tab => {
            const active = filter === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setFilter(tab.id)}
                style={{
                  padding: '7px 14px', borderRadius: 999,
                  border: `1.5px solid ${active ? colors.accent : colors.cardBorder}`,
                  backgroundColor: active ? colors.accent : colors.bg,
                  color: active ? '#020617' : colors.textSub,
                  fontSize: 12, fontWeight: 700,
                  minHeight: 44, cursor: 'pointer',
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 16 }}><SkeletonGrid items={6} /></div>
      ) : (
        <div style={{ padding: 16, paddingBottom: showFloatingCta ? 110 : 24 }}>
          {visibleGames.length === 0 ? (
            <EmptyState icon="gamepad" title={t('games.empty.title')} body={t('games.empty.body')} />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {visibleGames.map((item, idx) => {
                const downloadOnly = !allowsCredentialRequests(item);
                const status = statusByGame[item.id] || 'request';
                const subline = downloadOnly
                  ? t('games.card.download')
                  : status === 'ready' ? t('games.card.ready')
                    : status === 'pending' ? t('games.card.pending')
                      : status === 'rejected' ? t('games.card.rejected')
                        : t('games.card.request');
                const iconName = downloadOnly ? 'download'
                  : status === 'ready' ? 'key'
                    : status === 'pending' ? 'clock'
                      : status === 'rejected' ? 'exclamation-circle'
                        : 'paper-plane';
                const iconColor = status === 'rejected' ? '#dc2626' : item.isHot ? '#ef4444' : colors.textMuted;
                const onClick = () => downloadOnly
                  ? handleDownload(item.downloadLink!)
                  : status === 'ready' ? openCredentials(item)
                    : status === 'pending' ? alert(t('games.pending.title'), t('games.pending.message', { game: item.name }))
                      : status === 'rejected' ? showRejectedRequest(item)
                        : confirmCredentialRequest(item);
                return (
                  <button
                    key={item.id}
                    data-tour={idx === 0 ? 'game-card-first' : undefined}
                    onClick={onClick}
                    style={{
                      position: 'relative',
                      aspectRatio: 1,
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center',
                      padding: 12, borderRadius: 14,
                      backgroundColor: colors.card,
                      border: item.isHot
                        ? '1.5px solid #ef4444'
                        : `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`,
                      cursor: 'pointer', textAlign: 'center',
                      gap: 10,
                    }}
                  >
                    {item.isHot && (
                      <div style={{
                        position: 'absolute', top: 8, right: 8,
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        backgroundColor: '#ef4444', borderRadius: 999,
                        padding: '3px 8px',
                      }}>
                        <Icon name="fire" size={9} color="#fff" />
                        <span style={{ color: '#fff', fontSize: 9, fontWeight: 900 }}>{t('games.hotBadge')}</span>
                      </div>
                    )}
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt="" style={{ width: 62, height: 62, borderRadius: 14, objectFit: 'cover' }} />
                    ) : (
                      <div style={{
                        width: 62, height: 62, borderRadius: 14, backgroundColor: '#14b8a6',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Icon name="gamepad" size={22} color="#fff" />
                      </div>
                    )}
                    <div style={{
                      fontSize: 14, fontWeight: 800, color: colors.text,
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}>
                      {item.name}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted }}>{subline}</div>
                    <Icon name={iconName} size={13} color={iconColor} />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {showFloatingCta && (
        <div
          style={{
            position: 'fixed', left: 0, right: 0,
            bottom: `calc(68px + env(safe-area-inset-bottom, 0px))`,
            padding: 16, borderTop: `1px solid ${colors.cardBorder}`,
            backgroundColor: colors.bg, zIndex: 50,
          }}
        >
          <button
            onClick={() => setShowRequestModal(true)}
            style={{
              width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8,
              backgroundColor: colors.accent, color: '#020617',
              border: 'none', borderRadius: 999, padding: '16px 0',
              fontSize: 15, fontWeight: 800, cursor: 'pointer',
            }}
          >
            <Icon name="plus" size={13} color="#020617" />
            {t('games.requestCredentialsCta')}
          </button>
        </div>
      )}

      {/* Credentials Modal */}
      <Modal open={showModal} onClose={() => setShowModal(false)}>
        <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: isDark ? '#334155' : '#e2e8f0', margin: '0 auto 18px' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: colors.text }}>{selectedGame?.name}</div>
            <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>{t('games.credentialsModal.subtitle')}</div>
          </div>
          <button
            aria-label={t('games.credentialsModal.close')}
            onClick={() => setShowModal(false)}
            style={{
              width: 44, height: 44, borderRadius: 22,
              background: 'transparent', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginTop: -8, marginRight: -8,
            }}
          >
            <Icon name="times" size={18} color={colors.textMuted} />
          </button>
        </div>

        {credLoading ? (
          <div style={{ padding: 28, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 32, height: 32, border: `4px solid ${colors.accentSoft}`,
              borderTopColor: colors.accentDark, borderRadius: '50%',
              animation: 'aga-spin 0.7s linear infinite',
            }} />
            <div style={{ fontSize: 14, color: colors.textMuted, marginTop: 8 }}>{t('games.credentialsModal.loading')}</div>
          </div>
        ) : credentials.length === 0 ? (
          <div style={{ padding: 28, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center' }}>
            <Icon name="key" size={36} color={colors.textMuted} />
            <div style={{ fontSize: 16, fontWeight: 700, color: colors.textSub, marginTop: 6 }}>{t('games.credentialsModal.noCredentialsTitle')}</div>
            <div style={{ fontSize: 13, color: colors.textMuted, lineHeight: 1.5, padding: '0 16px' }}>
              {t('games.credentialsModal.noCredentialsBody')}
            </div>
            {selectedGame && (
              <button
                disabled={submittingRequest}
                onClick={() => confirmCredentialRequest(selectedGame)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  backgroundColor: '#14b8a6', color: '#020617',
                  border: 'none', borderRadius: 10,
                  padding: '10px 20px', marginTop: 6, minHeight: 44,
                  fontSize: 13, fontWeight: 800,
                  cursor: submittingRequest ? 'default' : 'pointer',
                  opacity: submittingRequest ? 0.5 : 1,
                }}
              >
                <Icon name="paper-plane" size={13} color="#020617" />
                {submittingRequest ? t('games.credentialsModal.sending') : t('games.requestCredentialsCta')}
              </button>
            )}
          </div>
        ) : (
          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            {credentials.length > 0 && (
              <CoachMark
                storageKey="credentials-modal-tip-v1"
                title={t('games.coachMark.title')}
                body={t('games.coachMark.body')}
                placement="bottom"
                style={{ marginBottom: 12 }}
              />
            )}
            {credentials.map(cred => (
              <div
                key={cred.id}
                style={{
                  borderRadius: 12, padding: 14, marginBottom: 10,
                  backgroundColor: isDark ? '#0f172a' : '#f8fafc',
                  border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`,
                }}
              >
                <CredRow icon="user-circle" label={t('games.credentialsModal.username')} value={cred.gameUsername} colors={colors} />
                <div style={{ marginTop: 10 }}>
                  <CredRow icon="lock" label={t('games.credentialsModal.password')} value={cred.gamePasswordPlain} colors={colors} />
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                  <CopyChip
                    label={copiedKey === `${cred.id}-username` ? t('games.credentialsModal.copied') : t('games.credentialsModal.username')}
                    copied={copiedKey === `${cred.id}-username`}
                    onClick={() => copyCredentialValue(`${cred.id}-username`, cred.gameUsername)}
                  />
                  <CopyChip
                    label={copiedKey === `${cred.id}-password` ? t('games.credentialsModal.copied') : t('games.credentialsModal.password')}
                    copied={copiedKey === `${cred.id}-password`}
                    onClick={() => copyCredentialValue(`${cred.id}-password`, cred.gamePasswordPlain)}
                  />
                  <CopyChip
                    label={copiedKey === `${cred.id}-both` ? t('games.credentialsModal.copied') : t('games.credentialsModal.both')}
                    copied={copiedKey === `${cred.id}-both`}
                    onClick={() => copyCredentialValue(`${cred.id}-both`, `Username: ${cred.gameUsername}\nPassword: ${cred.gamePasswordPlain}`)}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 10, marginBottom: 10 }}>
                  <Icon name="calendar-alt" size={11} color={colors.textMuted} />
                  <div style={{ fontSize: 11, color: colors.textMuted }}>
                    {t('games.credentialsModal.added', { date: new Date(cred.assignedAt).toLocaleDateString() })}
                  </div>
                </div>
                <button
                  disabled={requestingId === cred.id}
                  onClick={() => requestPasswordReset(cred)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    backgroundColor: '#fef3c7', border: '1px solid #fde68a',
                    borderRadius: 8, padding: '8px 12px', minHeight: 44,
                    color: '#d97706', fontSize: 12, fontWeight: 700,
                    cursor: requestingId === cred.id ? 'default' : 'pointer',
                    opacity: requestingId === cred.id ? 0.5 : 1,
                  }}
                >
                  <Icon name="key" size={12} color="#d97706" />
                  {requestingId === cred.id ? t('games.passwordReset.sending') : t('games.passwordReset.label')}
                </button>
              </div>
            ))}
          </div>
        )}

        {selectedGame?.downloadLink && (
          <Button label={t('games.credentialsModal.downloadGame')} icon="download" variant="outline" onClick={() => handleDownload(selectedGame.downloadLink!)} style={{ marginTop: 12 }} />
        )}
        <Button label={t('games.credentialsModal.close')} variant="secondary" onClick={() => setShowModal(false)} style={{ marginTop: 10 }} />
      </Modal>

      {/* Request Modal */}
      <Modal open={showRequestModal} onClose={() => setShowRequestModal(false)}>
        <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: isDark ? '#334155' : '#e2e8f0', margin: '0 auto 18px' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: colors.text }}>{t('games.requestModal.title')}</div>
            <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>{t('games.requestModal.subtitle')}</div>
          </div>
          <button
            aria-label={t('games.requestModal.close')}
            onClick={() => setShowRequestModal(false)}
            style={{
              width: 44, height: 44, borderRadius: 22,
              background: 'transparent', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginTop: -8, marginRight: -8,
            }}
          >
            <Icon name="times" size={18} color={colors.textMuted} />
          </button>
        </div>
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          {games
            .filter(game => allowsCredentialRequests(game) && ['request', 'rejected'].includes(statusByGame[game.id] || 'request'))
            .map(game => (
              <button
                key={game.id}
                onClick={() => confirmCredentialRequest(game)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                  padding: 12, marginBottom: 8, borderRadius: 12,
                  backgroundColor: isDark ? '#0f172a' : '#f8fafc',
                  border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`,
                  cursor: 'pointer', textAlign: 'left', minHeight: 64,
                }}
              >
                {game.imageUrl ? (
                  <img src={game.imageUrl} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }} />
                ) : (
                  <div style={{
                    width: 40, height: 40, borderRadius: 8, backgroundColor: '#14b8a6',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon name="gamepad" size={16} color="#fff" />
                  </div>
                )}
                <div style={{ flex: 1, fontSize: 14, fontWeight: 600, color: colors.text }}>{game.name}</div>
                <Icon name="paper-plane" size={13} color={colors.accentDark} />
              </button>
            ))}
        </div>
        <Button label={t('games.requestModal.cancel')} variant="secondary" onClick={() => setShowRequestModal(false)} style={{ marginTop: 12 }} />
      </Modal>
    </div>
  );
}

function CredRow({ icon, label, value, colors }: { icon: string; label: string; value: string; colors: any }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Icon name={icon} size={13} color={colors.accentDark} />
        <div style={{ fontSize: 12, fontWeight: 600, color: colors.textMuted }}>{label}</div>
      </div>
      <div style={{
        flexShrink: 1, marginLeft: 12, fontSize: 15, fontWeight: 600,
        color: colors.text, textAlign: 'right',
        wordBreak: 'break-all',
      }}>
        {value}
      </div>
    </div>
  );
}

function CopyChip({ label, copied, onClick }: { label: string; copied: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        backgroundColor: '#ccfbf1', border: 'none',
        borderRadius: 8, padding: '8px 10px', minHeight: 40,
        color: '#0f766e', fontSize: 11, fontWeight: 800,
        cursor: 'pointer',
      }}
    >
      <Icon name={copied ? 'check' : 'copy'} size={11} color="#0f766e" />
      {label}
    </button>
  );
}
